// src/pages/Users/PassageCeintureDocuments/PassageCeintureDocumentsPage.jsx

import React, { useState } from "react";
import { FaGraduationCap } from "react-icons/fa";

import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";

import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Reveal from "./../../../components/Reveal/Reveal";

import usePassageCeintureDocumentsController from "./hooks/usePassageCeintureDocumentsController";

import PassageCeintureDocumentsHero from "./components/PassageCeintureDocumentsHero";
import PassageCeintureDocumentsToolbar from "./components/PassageCeintureDocumentsToolbar";
import PassageCeintureDocumentCard from "./components/PassageCeintureDocumentCard";
import PassageCeintureDocumentSheet from "./components/PassageCeintureDocumentSheet";
import SkeletonDocumentCard from "./components/SkeletonDocumentCard";
import EmptyDocumentsState from "./components/EmptyDocumentsState";

export default function PassageCeintureDocumentsPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [sheet, setSheet] = useState({
    open: false,
    mode: "view", // view | create | edit
    document: null,
  });

  const {
    loading,
    allDocuments,
    belts,
    filtered,
    visible,
    ui,
    searchInput,
    setSearchInput,
    dynamicOptions,
    canManage,
    stats,
    updateUi,
    resetFilters,
    loadMore,
    refreshDocuments,
    deleteDocument,
  } = usePassageCeintureDocumentsController(user);

  const openView = (document) => {
    setSheet({
      open: true,
      mode: "view",
      document,
    });
  };

  const openCreate = () => {
    setSheet({
      open: true,
      mode: "create",
      document: null,
    });
  };

  const openEdit = (document) => {
    setSheet({
      open: true,
      mode: "edit",
      document,
    });
  };

  const closeSheet = () => {
    setSheet({
      open: false,
      mode: "view",
      document: null,
    });
  };

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-24 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Passage de ceinture"
          subtitle="Retrouve les documents, fiches et supports utiles pour préparer les passages de ceinture."
          icon={FaGraduationCap}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <Reveal>
          <PassageCeintureDocumentsHero
            totalCount={allDocuments.length}
            filteredCount={filtered.length}
            publishedCount={stats.published}
            draftCount={stats.drafts}
            canManage={canManage}
          />
        </Reveal>

        <div className="sticky top-[76px] z-30 -mx-3 mt-4 border-y border-slate-200/80 bg-slate-50/95 px-3 py-3 backdrop-blur-xl dark:border-slate-800 dark:bg-slate-950/90 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0 sm:backdrop-blur-0">
          <PassageCeintureDocumentsToolbar
            dynamicOptions={dynamicOptions}
            ui={ui}
            canManage={canManage}
            onChange={updateUi}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            onReset={resetFilters}
            onCreate={openCreate}
            filteredCount={filtered.length}
          />
        </div>

        <section className="mt-5">
          {loading && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {Array.from({ length: 12 }).map((_, index) => (
                <SkeletonDocumentCard key={index} />
              ))}
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <EmptyDocumentsState onReset={resetFilters} canManage={canManage} />
          )}

          {!loading && filtered.length > 0 && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {visible.map((document) => (
                  <PassageCeintureDocumentCard
                    key={document.id}
                    document={document}
                    canManage={canManage}
                    onOpen={() => openView(document)}
                    onEdit={() => openEdit(document)}
                    onDelete={() => deleteDocument(document)}
                  />
                ))}
              </div>

              {visible.length < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    className="inline-flex min-h-12 items-center justify-center rounded-2xl bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/10 transition hover:-translate-y-0.5 hover:bg-slate-800 active:translate-y-0 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
                  >
                    Charger plus de documents
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </main>

      <PassageCeintureDocumentSheet
        isOpen={sheet.open}
        mode={sheet.mode}
        readOnly={sheet.mode === "view"}
        document={sheet.document}
        belts={belts}
        canManage={canManage}
        onClose={closeSheet}
        onEdit={() => openEdit(sheet.document)}
        onSaved={async () => {
          await refreshDocuments();
          closeSheet();
        }}
      />
    </section>
  );
}